import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  BreadcrumbType,
  Card,
  closeCurrentDialog,
  DataStatus,
  openDialog,
  showNotification,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  useAddCodeMutation,
  useChangeOrderOfCodesMutation,
  useReadCodelistQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsTemplate, SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import {CodeForm, CodeFormProps} from './components/CodeForm';
import {CodelistItem} from './components/CodelistItem';
import {SortableCodelistItem} from './components/SortableCodelistItem';

interface CodelistDetailProps {
  onClose?: () => void;
  breadcrumbs?: BreadcrumbType[];
}

export function CodelistDetail(props: CodelistDetailProps) {
  const {id: codelistId} = useRequiredParams();
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const {data, isLoading, isError} = useReadCodelistQuery({codelistId});

  const [addCode] = useAddCodeMutation();
  const [changeOrder] = useChangeOrderOfCodesMutation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activeCodes = data?.codes.filter((code) => !code.isDisabled) ?? [];
  const itemIds = activeCodes.map((code) => code.codeId);

  const onDragStart = (event: DragStartEvent) => {
    setDraggedId(event.active.id.toString());
  };

  const onDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    setDraggedId(null);

    if (isNil(over) || active.id === over.id || isNil(data)) {
      return;
    }

    const oldIndex = activeCodes.findIndex((code) => code.codeId === active.id);
    const newIndex = activeCodes.findIndex((code) => code.codeId === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedCodes = arrayMove(activeCodes, oldIndex, newIndex);
      const reorderedCodeIds = reorderedCodes.map((c) => c.codeId);

      changeOrder({
        codelistId,
        changeOrderOfCodesRequestBody: {codeIds: reorderedCodeIds},
      })
        .unwrap()
        .catch(handleApiError);
    }
  };

  const draggedItem = draggedId ? activeCodes.find((code) => code.codeId === draggedId) : null;

  const onAddSubmit: CodeFormProps['onSubmit'] = (values) =>
    addCode({
      codelistId,
      addCodeRequestBody: {
        name: values.name,
        priority: 0,
      },
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
        showNotification.success(i18n.t('entity.codelist.notifications.itemAdded'));
      })
      .catch(handleApiError);

  const onAdd = () => {
    openDialog(
      <CodeForm
        onSubmit={onAddSubmit}
        data-testid={testIds.settings.codelistDetail('code-form')}
      />,
      {
        title: i18n.t('entity.codelist.labels.addItem'),
      }
    );
  };

  return (
    <SettingsTemplate
      isLoading={isLoading}
      isError={isError}
      header={{
        breadcrumbs: props.breadcrumbs,
        title: data?.name,
        actions: [
          {
            type: 'button',
            leftIcon: 'content/add_circle',
            title: i18n.t('entity.codelist.labels.addItem'),
            onClick: onAdd,
            'data-testid': testIds.settings.codelistDetail('add-new'),
          },
        ],
      }}
      data-testid={testIds.settings.codelistDetail('layout')}
    >
      <SettingsSection>
        <Card variant="inlineGrey" title={i18n.t('entity.checkout.labels.items')}>
          <DataStatus isEmpty={!data?.codes.length} minHeight={24}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            >
              <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                <VStack>
                  {activeCodes.map((code) => (
                    <SortableCodelistItem key={code.codeId} code={code} codelistId={codelistId} />
                  ))}
                </VStack>
              </SortableContext>
              <DragOverlay>
                {draggedItem ? (
                  <DragOverlayWrapper>
                    <CodelistItem code={draggedItem} codelistId={codelistId} />
                  </DragOverlayWrapper>
                ) : null}
              </DragOverlay>
            </DndContext>
          </DataStatus>
        </Card>
      </SettingsSection>

      <Show when={isNotNil(props.onClose)}>
        <SettingsFooter
          actions={[
            {
              type: 'button',
              title: i18n.t('general.actions.close'),
              onClick: props.onClose,
            },
          ]}
        />
      </Show>
    </SettingsTemplate>
  );
}

const DragOverlayWrapper = styled.div`
  cursor: grabbing;
`;
